/** Retry-Error Token SSOT: the canonical substring-match token sets the KV / R2 / HTTP retry classifiers compose into
 *  their domain-specific lists. Tokens match case-insensitively via `includes` against a lowercased message, so substring overlap is intentional; the non-transient set WINS on conflict in `makeTransientErrorClassifier`. */

/** Transient (retryable) server-side statuses. Same codes as `isRetryableStatus(..., { retryOn5xx: true })`, in substring form for classifiers that read error message TEXT rather than parsed status codes. */
export const TRANSIENT_HTTP_STATUS_TOKENS = ['500', '502', '503', '504'] as const;

/** Human-readable status messages for transient server failures. */
export const TRANSIENT_HTTP_STATUS_MESSAGE_TOKENS = [
	'internal error',
	'service unavailable',
	'gateway timeout',
	'bad gateway',
	'temporarily unavailable',
] as const;

/** Connection/timeout/abort vectors from `fetch` and Node-runtime adapters. All transient by definition: the request never reached the server or never got a response, so retry is the correct action. */
export const TRANSIENT_NETWORK_ERROR_MESSAGE_TOKENS = [
	'network error',
	'connection reset',
	'connection refused',
	'connection aborted',
	'connection closed',
	'connection error',
	'socket hang up',
	'timeout',
	'timed out',
	'etimedout',
	'network timeout',
	'fetch failed',
	'failed to fetch',
	'request aborted',
	'network disconnected',
] as const;

/**
 * JS-RPC / WebSocket TRANSPORT-DROP vocabulary: the peer closing the socket WITHOUT a Close frame (close code 1006).
 * Distinct from {@link TRANSIENT_NETWORK_ERROR_MESSAGE_TOKENS}, whose members are `fetch` and Node-adapter wordings --
 * measured 2026-08-27: not one of them is a substring of the workerd RPC drop message
 * `Peer closed WebSocket: 1006 WebSocket disconnected without sending Close frame.`, so an RPC drop classified
 * NON-transient under every existing classifier and reached the caller as a service refusal.
 *
 * TRANSIENT BY DEFINITION OF THE CODE: 1006 is the abnormal-closure code a peer NEVER sends on the wire -- the local
 * endpoint synthesizes it when the connection vanished before a Close handshake -- so the call was neither answered
 * nor refused, which is exactly the state a retry is for. A service that UNDERSTOOD and rejected a call answers
 * through its own error, never through this wording.
 *
 * THE `1006` DIGITS ARE DELIBERATELY NOT A TOKEN. These match as case-insensitive substrings against a whole message,
 * and a bare four-digit run appears inside ids, hashes, byte counts and timestamps -- a token that would retry a
 * genuine refusal whose message happened to contain those digits. The two WORDINGS are unambiguous on their own and
 * are the vendor's own, so the classifier reads them instead.
 */
export const TRANSIENT_RPC_TRANSPORT_DROP_MESSAGE_TOKENS = ['peer closed websocket', 'without sending close frame'] as const;

/**
 * The JS-RPC WebSocket's CONNECT failure, a WHOLE message rather than a token: the socket to the remote binding never
 * opened, so the call was neither answered nor refused. Witnessed verbatim 146 times in the code-catalog sync log as a
 * row's entire reason. A PAYLOAD compared by equality, never a substring token -- "connection failed" inside some other
 * sentence says nothing about this socket, which is why it is not a member of the token lists above.
 */
export const TRANSIENT_RPC_CONNECT_FAILED_MESSAGE = 'WebSocket connection failed.';

/** Non-transient client-side failures -- NOT retryable (caller bugs, auth failures, conflict states). Shared baseline; domain wrappers may extend it. */
export const NON_TRANSIENT_HTTP_STATUS_TOKENS = ['400', '401', '403', '404', '409'] as const;

/** Non-transient failures across R2 / KV / generic HTTP: retry would not change the outcome -- the request was understood and refused at the server. */
export const NON_TRANSIENT_HTTP_RESPONSE_MESSAGE_TOKENS = ['precondition failed', 'access denied', 'not found'] as const;

/** D1-specific transient tokens not covered by the subsets above: SQLite busy/lock states, the D1 overload signal, the storage-restart and storage-reset messages, and the socket-reset vectors. LOWERCASE form, consumed by the case-insensitive `isD1TransientError` and, ALONE (no timeout token, so a hung spawn is never re-run), by the post-apply PRAGMA optimize retry (`tools/migrations/migration-handler-post-optimize.ts`). The reset wording is the platform's own: `Internal error in D1 DB storage caused object to be reset`, witnessed on kaizen-dev after 0194 (iss-532875e2). */
export const D1_SQLITE_TRANSIENT_TOKENS = [
	'd1 db is overloaded',
	'too many requests queued',
	'sqlite_busy',
	'database is locked',
	'unable to open database',
	'connection lost',
	'econnreset',
	'internal error while starting up d1 db storage',
	'storage caused object to be reset',
] as const;
